import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Provider, useSelector, useDispatch } from 'react-redux';
import OutsideClickHandler from 'react-outside-click-handler';
import {
    createBrowserRouter,
    NavLink,
    Navigate,
    Outlet,
    RouterProvider,
    useNavigate,
    useLocation,
    useOutletContext,
    useParams,
} from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik } from 'formik';
import store, * as storeExt from './store';
import { WithHubConnectionContext } from './hubConnection';
import {
    HorizontalLine,
    InitialsButton,
    Pagination,
    Alert as RawAlert,
    ToolTip,
} from './components';
import { Project } from './pages/project';
import { Document } from './pages/document';
import { User } from './pages/user';
import VibroAcousticsLogo from './assets/svgs/vibro-acoustics-by-swegon.svg?react';
import editIcon from './assets/svgs/edit-b.svg';
import duplicateIcon from './assets/svgs/duplicate-b.svg';
import deleteIcon from './assets/svgs/trash-can-b.svg';
import { ViewListIcon, AddIcon } from './swegon-compat/icons';
import { WaitingModal } from './components/modals/WaitingModal';
import { DeleteProjectModal } from './components/modals/DeleteProjectModal';
import { NewProjectModal } from './components/modals/NewProjectModal';
import { EditProjectModal } from './components/modals/EditProjectModal';

const {
    useGetMeQuery,
    useUpdateMySettingsMutation,
    useGetProjectsQuery,
    useCopyProjectMutation,
    mainActions,
} = storeExt;

const Alert = ({ id, kind, msg }) => {
    const dispatch = useDispatch();

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            dispatch(storeExt.mainActions.clearAlert(id));
        }, 5000);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [dispatch, id]);

    return (
        <RawAlert
            kind={kind}
            msg={msg}
            onClose={() => dispatch(storeExt.mainActions.clearAlert(id))}
        />
    );
};

const Alerts = () => {
    const alerts = useSelector((state) => state.main.alerts);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (location.state) {
            const { alert, ...restState } = location.state;
            const state = Object.keys(restState).length > 0 ? restState : null;
            if (alert) {
                dispatch(storeExt.mainActions.addAlert(alert));
                navigate(location.pathname, { replace: true, state });
            }
        }
    }, [location.state, location.pathname, dispatch, navigate]);

    return alerts.map((alert) => (
        <Alert
            key={alert.id}
            {...alert}
        />
    ));
};

const UserMenu = (props) => {
    const { data: currentUser } = useGetMeQuery();
    const [menuHidden, setMenuHidden] = React.useState(true);
    const signOutFormRef = React.useRef();
    const navigate = useNavigate();

    return (
        <>
            <OutsideClickHandler
                onOutsideClick={() => {
                    if (!menuHidden) setMenuHidden(true);
                }}
            >
                <div className="relative">
                    <InitialsButton
                        onClick={() => {
                            setMenuHidden(!menuHidden);
                        }}
                    >
                        {currentUser.initials}
                    </InitialsButton>
                    <div
                        className={classNames(
                            'absolute w-max right-0 top-10 z-10',
                            { hidden: menuHidden },
                        )}
                    >
                        <div className="bg-white shadow-swegon3 w-fit rounded-swegonSmall p-4 border-none min-w-[15%]">
                            <div className="text-lg font-bold">
                                {currentUser.name}
                            </div>
                            <div className="text-sm text-black-75">
                                {currentUser.email}
                            </div>
                            <HorizontalLine />
                            <div
                                className="py-1 text-black-75 cursor-pointer flex flex-row items-center mt-1 hover:cursor-pointer hover:bg-green-secondary"
                                onClick={() => {
                                    navigate('/settings', { replace: true });
                                    setMenuHidden(true);
                                }}
                            >
                                <div className="w-8 flex justify-center justify-items-center">
                                    <ViewListIcon />
                                </div>
                                <div>Setttings</div>
                            </div>
                            <div
                                onClick={() => signOutFormRef.current.submit()}
                                className="py-1 text-black-75 cursor-pointer flex flex-row items-center mt-1 hover:cursor-pointer hover:bg-green-secondary"
                            >
                                <div className="w-8 flex justify-center justify-items-center">
                                    <FontAwesomeIcon icon="fa-solid fa-arrow-right-from-bracket" />
                                </div>
                                <div>Sign out</div>
                            </div>
                        </div>
                    </div>
                </div>
            </OutsideClickHandler>
            <form
                ref={signOutFormRef}
                className="hidden"
                method="POST"
                action="/api/sign-out"
            />
        </>
    );
};

const PageLayout = () => {
    const { data: currentUser } = useGetMeQuery();
    const { projectId } = useParams();
    const projectDocRoute =
        projectId === null || projectId === undefined
            ? '/documents'
            : `/documents/${projectId}`;
    const analysisRoute =
        projectId === null || projectId === undefined
            ? '/projects'
            : `/projects/${projectId}`;
    const usersRoute = '/users';
    const canEditUsers =
        currentUser?.roles.find((x) => {
            return x.code === 'EDIT_USERS';
        }) ?? false;
    // console.log(projectDocRoute);
    return (
        <>
            <header className="sticky top-0 bg-white z-10">
                <nav className="px-4 py-1 border-b border-green-secondary">
                    <div className="flex flex-wrap justify-between items-center mx-auto">
                        <div className="flex flex-wrap justify-between items-center">
                            <a
                                className="mr-8"
                                href="https://swegon.com"
                            >
                                <VibroAcousticsLogo />
                            </a>
                            <div>
                                <div className="flex flex-wrap justify-between">
                                    <NavLink
                                        to="/"
                                        className={({ isActive }) =>
                                            `${
                                                isActive
                                                    ? ' swegon-like-tab selected'
                                                    : ' swegon-like-tab'
                                            }`
                                        }
                                    >
                                        Home
                                    </NavLink>
                                    <NavLink
                                        to={analysisRoute}
                                        className={({ isActive }) =>
                                            `${
                                                isActive
                                                    ? ' swegon-like-tab selected'
                                                    : ' swegon-like-tab'
                                            }`
                                        }
                                    >
                                        Analysis
                                    </NavLink>
                                    <NavLink
                                        to={projectDocRoute}
                                        className={({ isActive }) =>
                                            `${
                                                isActive
                                                    ? ' swegon-like-tab selected'
                                                    : ' swegon-like-tab'
                                            }`
                                        }
                                    >
                                        Reporting
                                    </NavLink>
                                    {canEditUsers ? (
                                        <NavLink
                                            to={usersRoute}
                                            className={({ isActive }) =>
                                                `${
                                                    isActive
                                                        ? ' swegon-like-tab selected'
                                                        : ' swegon-like-tab'
                                                }`
                                            }
                                        >
                                            Users
                                        </NavLink>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <div className="flex">
                            {currentUser ? (
                                <UserMenu />
                            ) : (
                                <a
                                    href="/api/sign-in"
                                    className="text-black-75 hover:bg-black-10 focus:ring-2 focus:ring-black-10 rounded-swegonSmall px-4 py-2 mr-2"
                                >
                                    Sign in
                                </a>
                            )}
                        </div>
                    </div>
                </nav>
            </header>
            <div>{/* <SwegonButton>HJ</SwegonButton> */}</div>
            <div className="w-full flex flex-col sm:flex-row flex-grow overflow-hidden">
                <main
                    role="main"
                    className="w-full h-full flex-grow p-4 overflow-auto bg-black-5 z-0"
                >
                    <Alerts />
                    <Outlet />
                    <WaitingModal />
                </main>
            </div>
            <footer className="px-8 py-4 w-full border-y border-green-secondary">
                <div className="flex flex-wrap justify-between items-center mx-auto">
                    <div>
                        &copy; {new Date().getFullYear()} Swegon North America
                    </div>
                    <div>
                        <a href="mailto:jacob.brown@swegon.com">Contact us</a>
                    </div>
                </div>
            </footer>
        </>
    );
};

const Home = () => {
    const { data: currentUser, isError: getMeIsError } = useGetMeQuery();

    const hasAuthenticated = currentUser || getMeIsError;

    if (currentUser) {
        return <ProjectsAll />;
    }

    if (!hasAuthenticated) return null;

    return (
        <>
            <h1>VIRS Project Design</h1>
            <p>
                Please{' '}
                <a
                    className="underline text-blue-100 hover:text-blue-75"
                    href="/api/sign-in"
                >
                    sign in
                </a>{' '}
                to continue
            </p>
        </>
    );
};

const Projects = () => <Outlet />;

const ProjectsAll = () => {
    const { data: projects } = useGetProjectsQuery();
    const { data: currentUser } = useGetMeQuery();

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [copyProject] = useCopyProjectMutation();

    const [filteredProjects, setFilteredProjects] = useState(projects);
    const [showNewProjectModal, setShowNewProjectModal] = React.useState(false);
    const [showEditProjectModal, setShowEditProjectModal] = React.useState('');
    const [selectedOwner, setSelectedOwner] = useState('');
    const [selectedDateFilter, setSelectedDateFilter] = useState('anytime');
    const [selectedProject, setSelectedProject] = React.useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProjects
        ? filteredProjects.slice(indexOfFirstItem, indexOfLastItem)
        : filteredProjects;

    const handleCopyProject = async (projectId) => {
        let res = null;
        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: 'Copying Project...Please wait...',
            }),
        );
        try {
            res = await copyProject({ id: projectId }).unwrap();
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
            navigate(`/projects/${res.id}`);
        }
    };

    const handleSearch = useCallback(
        (searchString) => {
            setCurrentPage(1);

            if (!projects || !projects.length > 0) return;
            let filtered = projects;
            if (searchString) {
                filtered = filtered.filter((project) =>
                    project.name
                        .toLowerCase()
                        .includes(searchString.toLowerCase()),
                );
            }
            if (selectedOwner) {
                filtered = filtered.filter(
                    (project) => project.owner.id === selectedOwner,
                );
            }
            if (selectedDateFilter !== 'anytime') {
                const now = new Date();
                const filterMap = {
                    last7: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
                    last30: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
                    last90: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000),
                    thisYear: new Date(now.getFullYear(), 0, 1),
                    lastYear: new Date(now.getFullYear() - 1, 0, 1),
                };
                filtered = filtered.filter((project) => {
                    const updatedDate = new Date(project.updatedAt);
                    if (selectedDateFilter in filterMap) {
                        if (typeof filterMap[selectedDateFilter] === 'number') {
                            return (
                                updatedDate >=
                                new Date(
                                    now.setDate(
                                        now.getDate() -
                                            filterMap[selectedDateFilter],
                                    ),
                                )
                            );
                        }
                        return updatedDate >= filterMap[selectedDateFilter];
                    }
                    return true;
                });
            }
            setFilteredProjects(filtered);
        },
        [projects, selectedOwner, selectedDateFilter],
    );

    const handleDateFilterChange = (value) => {
        setSelectedDateFilter(value);
    };

    const handleOwnerFilterChange = (value) => {
        setSelectedOwner(value);
    };

    useEffect(() => {
        handleSearch('');
    }, [handleSearch, selectedDateFilter, selectedOwner]);

    useEffect(() => {
        setFilteredProjects(projects);
    }, [projects]);

    return (
        <div className="lg:container bg-white border border-black-10 p-8">
            <div className="flex items-center justify-between mb-4">
                <h1>VIRS Project Design</h1>
                <button
                    onClick={() => setShowNewProjectModal(true)}
                    className="swegon-like-button white"
                >
                    <AddIcon className="mr-2" />
                    <span>New Project</span>
                </button>
            </div>

            <table className="swegon-ui-table bordered hovered relative">
                <thead>
                    <tr>
                        <th className="swegon-ui-text-input-wrapper">
                            <input
                                type="text"
                                className="w-full"
                                onChange={(event) =>
                                    handleSearch(event.target.value)
                                }
                                placeholder="Search by Name..."
                            />
                        </th>
                        <th />
                        <th className="relative">
                            <select
                                className="swegon-like-button white"
                                onChange={(e) => {
                                    handleDateFilterChange(e.target.value);
                                }}
                            >
                                <option value="anytime">Anytime</option>
                                <option value="last7">Last 7 Days</option>
                                <option value="last30">Last 30 Days</option>
                                <option value="last90">Last 90 Days</option>
                                <option value="thisYear">This Year</option>
                                <option value="lastYear">Last Year</option>
                            </select>
                        </th>
                        <th className="relative">
                            <select
                                className="swegon-like-button white"
                                onChange={(e) => {
                                    handleOwnerFilterChange(e.target.value);
                                }}
                            >
                                <option value="">Everyone</option>
                                <option value={currentUser.id}>Me</option>
                                {/* <option value="">Shared</option> */}
                            </select>
                        </th>
                    </tr>
                    <tr>
                        <th className="p-2 relative flex">Name</th>
                        <th className="p-2">Created</th>
                        <th className="p-2">Updated</th>
                        <th className="p-2">Owner</th>
                    </tr>
                </thead>
                <tbody>
                    {currentItems && currentItems.length > 0 ? (
                        currentItems.map((project) => (
                            <tr
                                key={project.id}
                                className="border"
                                onDoubleClick={() =>
                                    navigate(`/projects/${project.id}`)
                                }
                            >
                                <td className="p-2 relative">{project.name}</td>
                                <td className="p-2 relative">
                                    {new Date(project.createdAt).toLocaleString(
                                        'en-US',
                                    )}
                                </td>
                                <td className="p-2 relative">
                                    {new Date(project.updatedAt).toLocaleString(
                                        'en-US',
                                    )}
                                </td>
                                <td className="p-2 relative">
                                    {project.owner.name}
                                </td>
                                <td className="justify-center">
                                    <ToolTip
                                        anchor={
                                            <button className="swegon-like-button white">
                                                <FontAwesomeIcon icon="fa-solid fa-ellipsis" />
                                            </button>
                                        }
                                        place="right-start"
                                        noArrow="true"
                                    >
                                        <div>
                                            <button
                                                className="swegon-like-button white flex"
                                                onClick={() =>
                                                    navigate(
                                                        `/projects/${project.id}`,
                                                    )
                                                }
                                            >
                                                <span>View</span>
                                                <img
                                                    className="w-5 h-5"
                                                    src={editIcon}
                                                    alt="Select"
                                                />
                                            </button>
                                            <button
                                                className="swegon-like-button white flex"
                                                onClick={() =>
                                                    setShowEditProjectModal(
                                                        project.id,
                                                    )
                                                }
                                            >
                                                <span>Edit Info</span>
                                                <img
                                                    className="w-5 h-5"
                                                    src={editIcon}
                                                    alt="Select"
                                                />
                                            </button>
                                            <button
                                                className="swegon-like-button white flex"
                                                onClick={() =>
                                                    handleCopyProject(
                                                        project.id,
                                                    )
                                                }
                                            >
                                                <span>Clone</span>
                                                <img
                                                    className="w-5 h-5"
                                                    src={duplicateIcon}
                                                    alt="Select"
                                                />
                                            </button>
                                            <button
                                                className="swegon-like-button white flex"
                                                onClick={() =>
                                                    setSelectedProject(project)
                                                }
                                            >
                                                <span>Delete</span>
                                                <img
                                                    className="w-5 h-5"
                                                    src={deleteIcon}
                                                    alt="Select"
                                                />
                                            </button>
                                        </div>
                                    </ToolTip>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td
                                colSpan={5}
                                className="p-4 text-center text-gray-500"
                            >
                                No projects
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            <div className="flex justify-between mt-4">
                <div>
                    <span>Show:</span>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(parseInt(e.target.value));
                            setCurrentPage(1); // Reset to first page when changing items per page
                        }}
                        className="ml-2 swegon-like-button white"
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                </div>
                {currentItems && currentItems.length > 0 ? (
                    <div>
                        <Pagination
                            currentPage={currentPage}
                            totalCount={filteredProjects.length}
                            itemsPerPage={itemsPerPage}
                            onPageChange={(page) => setCurrentPage(page)}
                        />
                    </div>
                ) : null}
            </div>

            {showNewProjectModal && (
                <NewProjectModal
                    show
                    onClose={() => setShowNewProjectModal(false)}
                />
            )}
            {showEditProjectModal !== '' && (
                <EditProjectModal
                    show
                    onClose={() => setShowEditProjectModal('')}
                    projectId={showEditProjectModal}
                />
            )}
            {selectedProject && (
                <DeleteProjectModal
                    project={selectedProject}
                    onClose={() => setSelectedProject(null)}
                />
            )}
            <WaitingModal />
        </div>
    );
};

const Documents = () => <Outlet />;

const DocumentsAll = () => <Outlet />;

const AuthRoute = () => {
    const { data: currentUser, isError: getMeIsError } = useGetMeQuery();
    const hasAuthenticated = currentUser || getMeIsError;

    const outletContext = useOutletContext();

    if (hasAuthenticated && !currentUser) {
        return (
            <Navigate
                to="/"
                replace={true}
                state={{ alert: { kind: 'error', msg: 'Please sign in' } }}
            />
        );
    }

    return <Outlet context={outletContext} />;
};

const Settings = () => {
    const { data: currentUser } = useGetMeQuery();
    const [updateMySetttings] = useUpdateMySettingsMutation();

    const initialValues = {
        unitType:
            currentUser?.settings?.find((x) => x.key === 'unitType')?.value ??
            'imperial',
        language:
            currentUser?.settings?.find((x) => x.key === 'language')?.value ??
            'en',
    };

    const handleSubmit = (values, formikHelpers) => {
        updateMySetttings(values);
        formikHelpers.setSubmitting(false);
    };

    return (
        <>
            <h1>Settings</h1>
            <div>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                >
                    {(formik) => (
                        <form onSubmit={formik.handleSubmit}>
                            <div className="mb-4">
                                <label
                                    className="block mb-2 text-sm font-medium"
                                    htmlFor="unitType"
                                >
                                    Unit Type
                                </label>
                                <select
                                    name="unitType"
                                    onChange={formik.handleChange}
                                    value={formik.values.unitType}
                                >
                                    <option value="imperial">Imperial</option>
                                    <option value="metric">Metric</option>
                                </select>
                            </div>
                            <div className="mb-4">
                                <label
                                    className="block mb-2 text-sm font-medium"
                                    htmlFor="language"
                                >
                                    Language
                                </label>
                                <select
                                    name="language"
                                    onChange={formik.handleChange}
                                    value={formik.values.language}
                                >
                                    <option value="en">English</option>
                                    <option value="sv">Swedish</option>
                                </select>
                            </div>
                            <button
                                className="text-white bg-blue-75 hover:bg-blue-90 active:bg-blue-100 hover:cursor-pointer focus:ring-4 focus:ring-blue-10 font-medium rounded-swegonSmall text-sm px-5 py-2.5 mr-2 mb-2"
                                type="submit"
                                disabled={formik.isSubmitting}
                            >
                                Submit
                            </button>
                        </form>
                    )}
                </Formik>
            </div>
        </>
    );
};

const router = createBrowserRouter([
    {
        path: '/',
        element: <PageLayout />,
        children: [
            {
                index: true,
                element: <Home />,
            },
            {
                element: <AuthRoute />,
                children: [
                    {
                        path: 'settings',
                        element: <Settings />,
                    },
                    {
                        path: 'projects',
                        element: <Projects />,
                        children: [
                            {
                                path: ':projectId',
                                element: <Project />,
                            },
                        ],
                    },
                    {
                        path: 'documents',
                        element: <Documents />,
                        children: [
                            {
                                index: true,
                                element: <DocumentsAll />,
                            },
                            {
                                path: ':projectId',
                                element: <Document />,
                            },
                        ],
                    },
                    {
                        path: 'users',
                        element: <User />,
                    },
                ],
            },
        ],
    },
]);

const App = () => {
    return (
        <WithHubConnectionContext opts={{ url: '/hubs/main' }}>
            <Provider store={store}>
                <RouterProvider router={router} />
            </Provider>
        </WithHubConnectionContext>
    );
};

export default App;
